import {BRANDS} from '#/lib/brand/brands'
import {readCachedBrandConfig} from '#/lib/brand/cache'
import {mergeBrandConfig} from '#/lib/brand/runtime'
import {type Brand, type BrandId} from '#/lib/brand/types'

export {BRANDS} from '#/lib/brand/brands'
export type {Brand, BrandId, BrandMarkElement} from '#/lib/brand/types'

/**
 * The build-time brand id. `EXPO_PUBLIC_*` vars are inlined by the bundler, so
 * a production bundle is permanently pinned to one brand:
 *
 *   EXPO_PUBLIC_BRAND=eagledrive npx expo export:web
 *
 * Unset/unknown falls back to cooey (the original deployment).
 */
const BRAND_ID = (process.env.EXPO_PUBLIC_BRAND || 'cooey') as BrandId

/**
 * The brand exactly as compiled into this bundle, before any runtime override.
 * This is the permanent fallback and the source of every field that is NOT
 * overridable (PDS, API base, SSO paths, mark geometry, scheme).
 */
export const COMPILED_BRAND: Brand = BRANDS[BRAND_ID] ?? BRANDS.cooey

/**
 * The active brand: the compiled record with the operator's runtime config
 * (`Brand.brandConfigUrl`) overlaid on top.
 *
 * ## Why the merge happens HERE, at module init
 *
 * Everything brand-derived in the app is a module-level constant evaluated at
 * import — `BRAND_NAME` below, the four feed constants in `#/lib/constants`,
 * and things derived from those in turn (e.g. `PWI_DISCOVER_FEED_STUB` in
 * `#/state/queries/feed`). Resolving the brand BEFORE any of them are
 * evaluated is therefore the one place a runtime override can be applied
 * consistently, with no consumer changes and no possibility of a half-applied
 * state where some screens use the operator's feeds and others the compiled
 * ones. It is the same shape as upstream's cached `geolocationServiceResponse`
 * (`#/geolocation/service`), read from the same synchronous device storage.
 *
 * That requires the config to be available SYNCHRONOUSLY, which is why the
 * source here is the device cache (MMKV: JSI on native, `localStorage` on web)
 * and never the network. `refreshBrandConfig()` runs in the background at app
 * start and refreshes that cache, so an operator's change lands on the next
 * cold start. Nothing on the render path ever waits for the network, so a
 * slow/dead/hostile admin host cannot delay or blank the app — the worst case
 * is that a brand-new install shows the compiled defaults for one session.
 *
 * For brands with no `brandConfigUrl` (cooey / eagledrive / yeet) this is the
 * compiled object itself, by identity — no storage read, no merge, no change
 * in behaviour whatsoever.
 */
export const BRAND: Brand = COMPILED_BRAND.brandConfigUrl
  ? // `readCachedBrandConfig` re-validates and returns null on any error
    mergeBrandConfig(COMPILED_BRAND, readCachedBrandConfig(COMPILED_BRAND.id))
  : COMPILED_BRAND

/** The product display name, for interpolation into UI strings. */
export const BRAND_NAME = BRAND.name
